"use client";
import DeferredVideo from "@/components/DeferredVideo";
import PrimaryBtn from "@/components/PrimaryBtn";
import SectionHeader from "@/components/SectionHeader";
import ServiceCard from "@/components/ServiceCard";
import { Icons, Images, Videos } from "@/app/assets/shared";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { services } from "./data";
import { whenIdle } from "@/lib/whenIdle";

// Lazy load GSAP only when needed
const loadGSAP = async () => {
  const gsapModule = await import("gsap");
  const scrollModule = await import("gsap/ScrollTrigger");

  const gsap = gsapModule.default;
  const ScrollTrigger = scrollModule.default;

  gsap.registerPlugin(ScrollTrigger);

  return { gsap };
};

export default function Services() {
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const sectionEl = sectionRef.current;
    if (!sectionEl || typeof window === "undefined") return;

    let ctx: any;
    let cancelled = false;

    // Idle-deferred: this section is below the fold, so ScrollTrigger setup
    // must not compete with hydration (mobile TBT / forced reflow).
    const cancelIdle = whenIdle(() => {
      if (cancelled) return;
      loadGSAP().then(({ gsap }) => {
        if (cancelled) return;
        ctx = gsap.context(() => {
          const boxes = sectionEl.querySelectorAll(".service-box");

          // 🔥 IMPORTANT: set initial state AFTER mount
          gsap.set(boxes, {
            y: 50,
            opacity: 0.01,
          });

          gsap.to(boxes, {
            y: 0,
            opacity: 1,
            ease: "power1.out",
            duration: 0.8,
            stagger: 0.2,
            scrollTrigger: {
              trigger: sectionEl,
              start: "top 80%",
              toggleActions: "play none none none",
            },
          });
        }, sectionEl);
      });
    });

    return () => {
      cancelled = true;
      cancelIdle();
      if (ctx) ctx.revert();
    };
  }, []);

  return (
    <section
      id="services"
      className="w-full relative overflow-hidden max-h-[max-content]"
      ref={sectionRef}
    >
      <Image
        src={Images?.serviceBg}
        alt="services bg"
        fill
        className="object-cover -z-10"
        priority={false}
        quality={75}
        sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
        fetchPriority="low"
      />
      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] py-[50px] lg:py-[150px]">
        <SectionHeader
          title={
            <>
              <span className="max-w-[24ch] block text-[length:inherit]">
                Redefine Your Digital Presence with Our
                <span className="text-[#4a84ff]">
                  {" "}
                  Future-Ready Solutions{" "}
                </span>{" "}
              </span>
            </>
          }
          subtitle="OUR SERVICES"
          dark={true}
          button={
            <PrimaryBtn
              text="View All"
              iconSrc={Icons?.chevronBlue}
              href={"/services"}
            />
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-[30px] pt-[0px] lg:pt-[100px] relative z-[2]">
          {/* <div className="hidden lg:block" /> */}
          {services?.map((item, index) => (
            <ServiceCard
              key={index}
              icon={item?.icon}
              icondark={item?.icondark}
              title={item?.title}
              description={item?.description}
              darkMode={false}
              link={item?.link}
            />
          ))}
        </div>

        <div className="absolute bottom-[40px] right-[40px] z-[1] hidden min-[1560px]:block">
          <div>
            <DeferredVideo
              src={Videos.blob}
              className={"mobile-toggle-video w-[350px] h-full object-cover"}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
